import React, { useCallback } from 'react';
import { Panel, useReactFlow, useNodesInitialized } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import layoutAlgorithms, { type Direction, type LayoutAlgorithmOptions } from '@/lib/layout/algorithms';
import { getSourceHandlePosition, getTargetHandlePosition } from '@/lib/layout/utils';
import { toast } from 'sonner';
import { IconProvider } from '../../../ui';

export interface AutoLayoutControlsProps {
  /** Position of the panel (only used if withPanel is true) */
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  /** Whether to wrap in a Panel component (default: true) */
  withPanel?: boolean;
}

/**
 * Auto-layout controls for React Flow
 * Provides a button to trigger automatic node layout with various algorithms and directions
 */
export function AutoLayoutControls({ position = 'top-right', withPanel = true }: AutoLayoutControlsProps) {
  const { fitView, setNodes, setEdges, getNodes, getEdges } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const [isOpen, setIsOpen] = React.useState(false);

  const handleAutoLayout = useCallback(async (
    algorithm: keyof typeof layoutAlgorithms,
    direction: Direction
  ) => {
    console.log('Auto-layout triggered:', algorithm, direction);
    
    // Close the menu
    setIsOpen(false);
    
    // Only run the layout if there are nodes and they have been initialized
    const nodes = getNodes();
    const edges = getEdges();
    
    if (!nodesInitialized || nodes.length === 0) {
      toast.error('No nodes to layout', {
        description: 'Add some nodes to the canvas first',
      });
      return;
    }

    try {
      const options: LayoutAlgorithmOptions = {
        direction,
        spacing: [50, 100],
      };

      const layoutAlgorithm = layoutAlgorithms[algorithm];
      // Clone nodes and edges to avoid mutating originals
      const nodesCopy = nodes.map(node => ({...node}));
      const edgesCopy = edges.map(edge => ({...edge}));

      const { nodes: nextNodes, edges: nextEdges } = await layoutAlgorithm(
        nodesCopy,
        edgesCopy,
        options
      );

      // Update node positions and handle positions
      for (const node of nextNodes) {
        node.style = { ...node.style, opacity: 1 };
        node.sourcePosition = getSourceHandlePosition(direction);
        node.targetPosition = getTargetHandlePosition(direction);
      }

      for (const edge of nextEdges) {
        edge.style = { ...edge.style, opacity: 1 };
      }

      setNodes(nextNodes);
      setEdges(nextEdges);
      
      // Fit view after layout
      setTimeout(() => {
        fitView({ padding: 0.2, duration: 300 });
      }, 50);

      toast.success('Layout applied successfully', {
        description: `Applied ${algorithm} layout in ${getDirectionLabel(direction)} direction`,
      });
    } catch (error) {
      console.error('Error applying layout:', error);
      toast.error('Failed to apply layout', {
        description: 'An error occurred while arranging nodes',
      });
    }
  }, [fitView, setNodes, setEdges, getNodes, getEdges, nodesInitialized]);

  const getDirectionLabel = (direction: Direction) => {
    switch (direction) {
      case 'TB':
        return 'top-to-bottom';
      case 'LR':
        return 'left-to-right';
      case 'BT':
        return 'bottom-to-top';
      case 'RL':
        return 'right-to-left';
    }
  };

  const content = (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="iconButton"
        >
          <IconProvider iconstring="fa-diagram-project" style="light" size={16}/>
         
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Choose Layout & Direction</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Dagre Algorithm */}
        <DropdownMenuItem onSelect={() => handleAutoLayout('dagre', 'TB')}>
          <IconProvider iconstring="fa-sitemap" className="mr-2 h-4 w-4" />
          Dagre (Top → Bottom)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleAutoLayout('dagre', 'LR')}>
          <IconProvider iconstring="fa-sitemap" className="mr-2 h-4 w-4" />
          Dagre (Left → Right)
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        
        {/* D3 Hierarchy Algorithm */}
        <DropdownMenuItem onSelect={() => handleAutoLayout('d3-hierarchy', 'TB')}>
          <IconProvider iconstring="fa-tree" className="mr-2 h-4 w-4" />
          D3 Tree (Top → Bottom)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleAutoLayout('d3-hierarchy', 'LR')}>
          <IconProvider iconstring="fa-tree" className="mr-2 h-4 w-4" />
          D3 Tree (Left → Right)
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />

        {/* ELK Algorithm */}
        <DropdownMenuItem onSelect={() => handleAutoLayout('elk', 'TB')}>
          <IconProvider iconstring="fa-layer-group" className="mr-2 h-4 w-4" />
          ELK (Top → Bottom)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => handleAutoLayout('elk', 'LR')}>
          <IconProvider iconstring="fa-layer-group" className="mr-2 h-4 w-4" />
          ELK (Left → Right)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return withPanel ? (
    <Panel position={position} className="m-2">
      {content}
    </Panel>
  ) : content;
}
